import React, { useMemo } from 'react';
import cn from 'clsx';

import styles from './ratings-chart.scss';

interface RatingsChartProps {
  className?: string;
  formattedValue?: string;
  hideFill?: boolean;
  hideLegendNumbers?: boolean;
  hideValue?: boolean;
  hideValueMarker?: boolean;
  scale?: number;
  showZeroValue?: boolean;
  value?: number;
  valueMarker?: React.ReactNode;
}

function RatingsChart(props: RatingsChartProps) {
  const {
    className = '',
    formattedValue: formattedValueProp,
    hideFill = false,
    hideLegendNumbers = false,
    hideValue = false,
    hideValueMarker = false,
    scale = 5,
    showZeroValue = false,
    value = 0,
    valueMarker
  } = props;
  const multiplier = 100 / scale;
  const widthPercentage = Math.max(0, value) * multiplier;
  const formattedValue =
    typeof value?.toFixed === 'function' ? value.toFixed(1) : value;
  const displayValue = formattedValueProp || formattedValue;

  const { legendArray = [], markerArray = [] } = useMemo(() => {
    const legendArray = Array.from({ length: scale + 1 }, (_, i) => i);
    const markerArray = legendArray.slice(1, -1);

    return { legendArray, markerArray };
  }, [scale]);

  return (
    <div className={cn(styles['container'], `ratings-chart ${className}`)}>
      <div
        className={cn(
          styles['background-outline'],
          'ratings-chart__background-outline'
        )}
      >
        <div className={cn(styles['background'], 'ratings-chart__background')}>
          {!hideFill && (
            <div
              className={cn(styles['fill'], 'ratings-chart__fill')}
              style={{ width: `${widthPercentage}%` }}
            />
          )}
          <div className={styles['marks']}>
            {markerArray.map(number => (
              <span
                className={cn(styles['mark'], 'ratings-chart__mark')}
                key={number}
              />
            ))}
          </div>
        </div>
      </div>
      {!hideLegendNumbers && (
        <div className={cn(styles['series'], 'ratings-chart__series')}>
          {legendArray.map(number => (
            <span className={styles['legend']} key={number}>
              {number}
            </span>
          ))}
        </div>
      )}

      {(value !== 0 || showZeroValue) && (
        <div
          className={cn(styles['value'], 'ratings-chart__value')}
          data-testid="ratings-chart__value"
          style={{
            left: `${widthPercentage}%`
          }}
        >
          {!hideValue && displayValue}
          {!valueMarker && !hideValueMarker ? (
            <>
              <span
                className={cn(styles['triangle'], 'ratings-chart__triangle')}
              />
              <span
                className={cn(
                  styles['value-mark'],
                  'ratings-chart__value-mark'
                )}
              />
            </>
          ) : (
            valueMarker
          )}
        </div>
      )}
    </div>
  );
}

export default RatingsChart;
